import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { CheckboxField } from "src/components/Form/fields/CheckboxField.tsx";
import { SwitchField } from "src/components/Form/fields/SwitchField.tsx";
import { RadioField } from "src/components/Form/fields/RadioField.tsx";
import { SingleSelectField } from "src/components/Form/fields/SingleSelectField.tsx";
import { NumberField } from "src/components/Form/fields/NumberField.tsx";
import { TextField } from "src/components/Form/fields/TextField.tsx";
import { MultiSelectField } from "src/components/Form/fields/MultiSelectField.tsx";
import { CkeditorField } from "src/components/Form/fields/CkeditorField.tsx";
import { ImageField } from "src/components/Form/fields/ImageField.tsx";
import { CustomField } from "src/components/Form/fields/CustomField.tsx";
import { DatePickerField } from "src/components/Form/fields/DatePickerField.tsx";

import { TextAreaField } from "src/components/Form/fields/TextAreaField.tsx";
import React from "react";
import { useResourceFormContext as useFormFrameworkContext } from "src/components/Form/hooks/useResourceFormContext.ts";
import { DateRangeField } from "src/components/Form/fields/DateRangeField.tsx";
import { type Control } from "react-hook-form";
import { SelectField } from "src/components/Form/fields/SelectField.tsx";
import { useIsFieldHidden } from "src/components/Form/hooks/useIsFieldHidden.ts";
import { useIsFieldDisabled } from "src/components/Form/hooks/useIsFieldDisabled.ts";
import { AutocompleteField } from "src/components/Form/fields/AutocompleteField.tsx";
import { ImageFieldV2 } from "src/components/Form/fields/ImageFieldV2.tsx";
import { TimePickerField } from "src/components/Form/fields/TimePickerField.tsx";
import { TimeRangePickerField } from "src/components/Form/fields/TimeRangePickerField.tsx";
import { getFieldConfig } from "src/components/Form/utils/getFieldConfig.ts";
import { RangeField } from "src/components/Form/panels/DefaultFormPanel/RangeField.tsx";
import { GroupField } from "src/components/Form/panels/DefaultFormPanel/GroupField.tsx";

export const FormField = <
  FormValues extends FieldValues = FieldValues,
  TName extends FieldPath<FormValues> = FieldPath<FormValues>,
>({
  name,
  control: controlProp,
}: {
  name: TName;
  control?: Control<FormValues>;
}) => {
  let node = null;
  const form = useFormContext<FormValues>();
  const { control: controlFromContext } = form;
  const control = controlProp || controlFromContext;
  const { fields, locale } = useFormFrameworkContext();

  const field = getFieldConfig<FormValues, TName>(fields, name);

  const { isHidden } = useIsFieldHidden({
    name,
    control,
  });

  const {
    forcedValue,
    isDisabled,
    tooltip: disabledTooltip,
  } = useIsFieldDisabled({
    name,
    control,
  });

  if (isHidden) {
    return null;
  }

  if (!field) {
    throw new Error(`Field [${name}] is not found`);
  }

  if (field.type === "checkbox") {
    node = (
      <CheckboxField
        control={control}
        disabled={isDisabled}
        key={name}
        name={name}
        onChange={(event) => field.onChange?.(event.target.checked, form, name)}
        floatingError={field.floatingError}
        forceValue={isDisabled ? forcedValue : undefined}
        additionalElements={field.additionalElements}
        label={field.label}
        helperText={field.helperText}
      />
    );
  }
  if (field.type === "switch") {
    node = (
      <SwitchField<FormValues, TName>
        disabled={isDisabled}
        control={control}
        key={name}
        name={name}
        helperText={field.helperText}
        // @ts-ignore
        defaultValue={field.defaultValue}
        label={field.label}
      />
    );
  }

  if (field.type === "radio") {
    node = (
      <RadioField
        key={name}
        control={control}
        name={name}
        // @ts-ignore
        defaultValue={field.defaultValue}
        label={field.label}
        orientation={field.orientation}
        options={field.options}
        disabled={isDisabled}
      />
    );
  }
  if (field.type === "singleSelect") {
    node = (
      <SingleSelectField
        allowClearValue={field.allowClearValue}
        control={control}
        locale={locale}
        isButton={field.isButton}
        placeholder={field.placeholder}
        floatingError={field.floatingError}
        forceValue={isDisabled ? forcedValue : undefined}
        options={field.options}
        onChange={field.onChange}
        helperText={field.helperText}
        serverOptions={field.serverOptions}
        defaultValue={field.defaultValue}
        label={field.label}
        endAdornment={field.endAdornment}
        startAdornment={field.startAdornment}
        additionalLabelElems={field.additionalLabelElems}
        disabled={isDisabled}
        key={name}
        name={name}
      />
    );
  }
  if (field.type === "select") {
    node = (
      <SelectField
        control={control}
        forceValue={isDisabled ? forcedValue : undefined}
        options={field.options}
        floatingError={field.floatingError}
        locale={locale}
        isMultiple={field.isMultiple}
        getInitialOptionsFromMainResource={
          field.getInitialOptionsFromMainResource
        }
        helperText={field.helperText}
        serverOptions={field.serverOptions}
        defaultValue={field.defaultValue}
        label={field.label}
        allowClearValue={field.allowClearValue}
        endAdornment={field.endAdornment}
        startAdornment={field.startAdornment}
        disabled={isDisabled}
        optionsMinWidth={field.optionsMinWidth}
        showAdvancedSearch={field.showAdvancedSearch}
        onChange={field.onChange}
        key={name}
        name={name}
        isButton={field.isButton}
        placeholder={field.placeholder}
      />
    );
  }
  if (field.type === "number") {
    node = (
      <NumberField
        disabled={isDisabled}
        disabledTooltip={disabledTooltip}
        control={control}
        key={name}
        floatingError={field.floatingError}
        name={name}
        placeholder={field.placeholder}
        endAdornment={field.endAdornment}
        forceValue={isDisabled ? forcedValue : undefined}
        precision={field.precision}
        // @ts-ignore
        defaultValue={field.defaultValue}
        controls={field.controls}
        helperText={field.helperText}
        label={field.label}
        additionalLabelElems={field.additionalLabelElems}
      />
    );
  }
  if (field.type === "text") {
    node = (
      <TextField
        disabled={isDisabled}
        control={control}
        floatingError={field.floatingError}
        key={name}
        labelWidth={field.labelWidth}
        readOnly={field.readOnly}
        placeholders={field.placeholders}
        forceValue={isDisabled ? forcedValue : undefined}
        name={name}
        placeholder={field.placeholder}
        // @ts-ignore
        defaultValue={field.defaultValue}
        required={field.required}
        helperText={field.helperText}
        endAdornment={field.endAdornment}
        startAdornment={field.startAdornment}
        label={field.label}
        additionalLabelElems={field.additionalLabelElems}
      />
    );
  }

  if (field.type === "autocomplete") {
    node = <AutocompleteField name={name} field={field} />;
  }
  if (field.type === "multiSelect") {
    return (
      <MultiSelectField
        control={control}
        locale={locale}
        floatingError={field.floatingError}
        key={name}
        showSearch={field.showSearch}
        name={name}
        // @ts-ignore
        defaultValue={field.defaultValue}
        helperText={field.helperText}
        disabled={isDisabled}
        serverOptions={field.serverOptions}
        options={field.options}
        label={field.label}
      />
    );
  }
  if (field.type === "ckeditor") {
    node = (
      <CkeditorField
        disabled={isDisabled}
        key={name}
        name={name}
        field={field}
      />
    );
  }
  if (field.type === "image") {
    node = (
      <ImageField disabled={isDisabled} key={name} name={name} field={field} />
    );
  }
  if (field.type === "imageV2") {
    node = (
      <ImageFieldV2
        disabled={isDisabled}
        key={name}
        name={name}
        field={field}
      />
    );
  }
  if (field.type === "custom") {
    node = <CustomField key={name} name={name} field={field} />;
  }
  if (field.type === "date") {
    node = (
      <DatePickerField
        floatingError={field.floatingError}
        disabled={isDisabled}
        name={name}
        field={field}
      />
    );
  }

  if (field.type === "timeRange") {
    node = (
      <TimeRangePickerField
        floatingError={field.floatingError}
        disabled={isDisabled}
        name={name}
        field={field}
      />
    );
  }

  if (field.type === "time") {
    node = (
      <TimePickerField
        floatingError={field.floatingError}
        disabled={isDisabled}
        name={name}
        field={field}
      />
    );
  }

  if (field.type === "textArea") {
    node = (
      <TextAreaField
        disabled={isDisabled}
        name={name}
        resize={field.resize}
        rootGap={field.rootGap}
        maxLength={field.maxLength}
        placeholdersPlacement={field.placeholdersPlacement}
        style={{
          height: field.height,
        }}
        placeholders={field.placeholders}
        floatingError={field.floatingError}
        label={field.label}
        helperText={field.helperText}
        placeholder={field.placeholder}
      />
    );
  }

  if (field.type === "dateRange") {
    node = (
      <DateRangeField
        name={name}
        shortcuts={field.shortcuts}
        floatingError={field.floatingError}
        label={field.label}
        // @ts-ignore
        defaultValue={field.defaultValue}
        disabled={isDisabled}
      />
    );
  }
  if (field.type === "inputRange") {
    node = <RangeField field={field} />;
  }
  if (field.type === "group") {
    node = <GroupField name={field} />;
  }
  return node;
};
